import React from 'react';
import { IField } from '@frontend/app/containers/Projects/LandingPages/types';
import { FieldType } from '@frontend/app/types/globalTypes';
import { isArray } from 'lodash';
import { CalendarIcon } from '@revfluence/fresh-icons/solid/esm';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@frontend/shadcn/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@frontend/shadcn/components/ui/popover';
import { Button } from '@frontend/shadcn/components/ui/button';
import { Calendar } from '@frontend/shadcn/components/ui/calendar';
import { Input } from '@frontend/shadcn/components/ui/input';
import { cn } from '@frontend/shadcn/lib/utils';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormMessage,
} from '@frontend/shadcn/components/ui/form';
import DOMPurify from 'dompurify';
import { P } from '@frontend/shadcn/components/typography/p';
import { Alert, AlertTitle } from '@frontend/shadcn/components/ui/alert';
import { ViewMode } from '../../constants';
import { MultiSelect } from './MultiSelect';
import { getSocialIcon } from '../../utils/getSocialIcon';

interface FormsProps {
  view: ViewMode;
  title?: string;
  description?: string;
  bodyTextColor?: string;
  backgroundColor?: string;
  cardColor?: string;
  buttonColor?: string;
  buttonTextColor?: string;
  buttonLabel?: string;
  buttonFontSize?: number;
  buttonFontFamily?: string;
  fields?: IField[];
  footerBrand?: string;
  footerLinks?: { label: string; href: string }[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSubmit?: (values: Record<string, any>) => void;
  socialMedia: {
    platform: string;
    url: string;
  }[];
  isPublished: boolean;
}

// Helper to generate Zod schema and default values from IField[]
const generateFormSchemaAndDefaults = (fields: IField[] = []) => {
  const schemaShape: Record<string, z.ZodTypeAny> = {};
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const defaultValues: Record<string, any> = {};

  fields.forEach((field) => {
    const fieldName = field.name || field.dbName || String(field.id);
    let baseSchema: z.ZodTypeAny;

    switch (FieldType[field.type as keyof typeof FieldType]) {
      case FieldType.TEXT:
      case FieldType.EMAIL:
        baseSchema = z.string();
        defaultValues[fieldName] = '';
        if (field.required) {
          schemaShape[fieldName] = (baseSchema as z.ZodString).min(1, {
            message: `${field.displayLabel || field.label || fieldName} is required.`,
          });
        } else {
          schemaShape[fieldName] = baseSchema.optional();
        }
        break;
      case FieldType.NUMBER:
      case FieldType.PERCENTAGE:
        baseSchema = z.coerce.number();
        defaultValues[fieldName] = undefined;
        if (field.required) {
          schemaShape[fieldName] = z.coerce
            .number({ required_error: `${field.displayLabel || field.label || fieldName} is required.` })
            .min(0);
        } else {
          schemaShape[fieldName] = baseSchema.optional();
        }
        break;
      case FieldType.BOOLEAN:
        baseSchema = z.boolean();
        defaultValues[fieldName] = undefined;
        if (field.required) {
          schemaShape[fieldName] = z.boolean({ required_error: `${field.displayLabel || field.label || fieldName} is required.` });
        } else {
          schemaShape[fieldName] = baseSchema.optional();
        }
        break;
      case FieldType.DATE:
      case FieldType.ANNUAL:
        baseSchema = z.date();
        defaultValues[fieldName] = null;
        if (field.required) {
          schemaShape[fieldName] = z.date({ required_error: `${field.displayLabel || field.label || fieldName} is required.` });
        } else {
          schemaShape[fieldName] = baseSchema.nullable().optional();
        }
        break;
      case FieldType.ARRAY:
        baseSchema = z.array(z.string());
        defaultValues[fieldName] = [];
        if (field.required) {
          schemaShape[fieldName] = (baseSchema as z.ZodArray<z.ZodString>).nonempty({
            message: `${field.displayLabel || field.label || fieldName} requires at least one selection.`,
          });
        } else {
          schemaShape[fieldName] = baseSchema.optional();
        }
        break;
      default:
        if (isArray(field.choices) && field.choices.length > 0) {
          baseSchema = z.string();
          defaultValues[fieldName] = '';
          if (field.required) {
            schemaShape[fieldName] = (baseSchema as z.ZodString).min(1, {
              message: `${field.displayLabel || field.label || fieldName} is required.`,
            });
          } else {
            schemaShape[fieldName] = baseSchema.optional();
          }
        } else {
          schemaShape[fieldName] = z.string().optional();
          defaultValues[fieldName] = '';
        }
    }
  });

  return {
    schema: z.object(schemaShape),
    defaultValues,
  };
};

const getPlaceholderForFieldNameFromConfig = (name: string, required?: boolean) => {
  return `${name}${required ? ' *' : ''}`;
};

export function Forms({
  view,
  title = 'Join Us!',
  description = 'We would love for you to consider joining our family. Our goal is to create a better way to communicate and learn from our customers and fans so that we can ultimately better serve your needs. Apply Now!',
  backgroundColor = '#b28d7b',
  cardColor = '#f5f1ed',
  bodyTextColor = '#5b2323',
  buttonColor = '#5b2323',
  buttonTextColor = 'white',
  buttonLabel = 'Submit Application',
  buttonFontSize,
  buttonFontFamily,
  fields = [],
  footerBrand = 'Aspire',
  footerLinks = [
    { label: 'Terms of Service', href: '#' },
    { label: 'Privacy Policy', href: '#' },
  ],
  onSubmit,
  socialMedia,
  isPublished,
}: FormsProps) {
  const isDesktop = view === ViewMode.DESKTOP;

  const { schema, defaultValues } = React.useMemo(() => generateFormSchemaAndDefaults(fields), [fields]);

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues,
  });

  const handleFormSubmit = (values: z.infer<typeof schema>) => {
    if (onSubmit) {
      onSubmit(values);
    }
  };

  const buttonStyle = {
    background: buttonColor,
    color: buttonTextColor,
    fontSize: buttonFontSize,
    fontFamily: buttonFontFamily,
  };

  return (
    <div
      className={`relative w-full min-h-screen flex flex-col items-center justify-center${isDesktop ? ' py-16' : ''}`}
      style={{ background: backgroundColor, color: bodyTextColor }}
    >
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(handleFormSubmit)}
          className={
            isDesktop
              ? 'rounded-2xl shadow-lg max-w-md w-full p-8 flex flex-col gap-4'
              : 'rounded-xl shadow-lg max-w-xs w-full p-4 flex flex-col gap-3 my-4'
          }
          style={{ background: cardColor }}
        >
          <div
            className={isDesktop ? 'text-xl font-semibold text-center mb-2' : 'text-lg font-semibold text-center mb-1'}
          >
            {title}
          </div>
          <div
            className={
              isDesktop ? 'text-base text-center mb-6 text-gray-800' : 'text-sm text-center mb-4 text-gray-800'
            }
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(description || '') }}
          />
          {fields.map((fieldConfig) => {
            const fieldName = fieldConfig.name || fieldConfig.dbName || String(fieldConfig.id);
            const fieldTypeEnum = FieldType[fieldConfig.type as keyof typeof FieldType];

            return (
              <FormField
                control={form.control}
                key={fieldConfig.id}
                name={fieldName}
                render={({ field: formHookField }) => (
                  <FormItem>
                    <FormControl>
                      {(() => {
                        if (
                          fieldTypeEnum === FieldType.ARRAY &&
                          isArray(fieldConfig.choices) &&
                          fieldConfig.choices.length > 0
                        ) {
                          return (
                            <MultiSelect
                              options={fieldConfig.choices.map((choice) => ({ label: choice, value: choice }))}
                              value={formHookField.value || []}
                              onChange={formHookField.onChange}
                              placeholder={getPlaceholderForFieldNameFromConfig(
                                fieldConfig.displayLabel || fieldConfig.label || fieldName,
                                fieldConfig.required,
                              )}
                            />
                          );
                        }
                        if (isArray(fieldConfig.choices) && fieldConfig.choices.length > 0) {
                          return (
                            <Select
                              onValueChange={formHookField.onChange}
                              defaultValue={formHookField.value as string | undefined}
                              value={formHookField.value as string | undefined}
                              disabled={false}
                              name={formHookField.name}
                            >
                              <SelectTrigger className="w-full h-12 bg-white data-[placeholder]:text-grey-4">
                                <SelectValue
                                  placeholder={getPlaceholderForFieldNameFromConfig(
                                    fieldConfig.displayLabel || fieldConfig.label || fieldName,
                                    fieldConfig.required,
                                  )}
                                />
                              </SelectTrigger>
                              <SelectContent>
                                {fieldConfig.choices.map((choice) => (
                                  <SelectItem value={choice} key={choice}>
                                    {choice}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          );
                        } else {
                          switch (fieldTypeEnum) {
                            case FieldType.BOOLEAN:
                              return (
                                <Select
                                  onValueChange={(value) => formHookField.onChange(value === 'yes')}
                                  value={
                                    formHookField.value === undefined ? undefined : formHookField.value ? 'yes' : 'no'
                                  }
                                  disabled={false}
                                  name={formHookField.name}
                                >
                                  <SelectTrigger className="w-full h-12 bg-white data-[placeholder]:text-grey-4">
                                    <SelectValue
                                      placeholder={getPlaceholderForFieldNameFromConfig(
                                        fieldConfig.displayLabel || fieldConfig.label || fieldName,
                                        fieldConfig.required,
                                      )}
                                    />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="yes">Yes</SelectItem>
                                    <SelectItem value="no">No</SelectItem>
                                  </SelectContent>
                                </Select>
                              );
                            case FieldType.DATE:
                            case FieldType.ANNUAL:
                              return (
                                <Popover>
                                  <PopoverTrigger asChild>
                                    <Button
                                      variant="outline"
                                      className={cn(
                                        'w-full h-12 justify-start text-left font-normal bg-white',
                                        !formHookField.value && 'text-grey-4',
                                      )}
                                      disabled={false}
                                    >
                                      <CalendarIcon className="mr-2 h-4 w-4" />
                                      {formHookField.value instanceof Date ? (
                                        formHookField.value.toLocaleDateString()
                                      ) : (
                                        <span>
                                          {getPlaceholderForFieldNameFromConfig(
                                            fieldConfig.displayLabel || fieldConfig.label || fieldName,
                                            fieldConfig.required,
                                          )}
                                        </span>
                                      )}
                                    </Button>
                                  </PopoverTrigger>
                                  <PopoverContent className="w-auto p-0">
                                    <Calendar
                                      mode="single"
                                      selected={formHookField.value instanceof Date ? formHookField.value : undefined}
                                      onSelect={formHookField.onChange}
                                      initialFocus
                                      disabled={false}
                                    />
                                  </PopoverContent>
                                </Popover>
                              );
                            case FieldType.TEXT:
                            case FieldType.EMAIL:
                            case FieldType.NUMBER:
                            case FieldType.PERCENTAGE:
                            default:
                              const inputType =
                                fieldTypeEnum === FieldType.EMAIL
                                  ? 'email'
                                  : fieldTypeEnum === FieldType.NUMBER || fieldTypeEnum === FieldType.PERCENTAGE
                                    ? 'number'
                                    : 'text';
                              return (
                                <div className="relative">
                                  <Input
                                    type={inputType}
                                    disabled={false}
                                    placeholder={getPlaceholderForFieldNameFromConfig(
                                      fieldConfig.displayLabel || fieldConfig.label || fieldName,
                                      fieldConfig.required,
                                    )}
                                    {...formHookField}
                                    className={cn('w-full h-12 bg-white')}
                                  />
                                </div>
                              );
                          }
                        }
                      })()}
                    </FormControl>
                    <FormDescription>
                      {/* Placeholder for field description, if available in fieldConfig e.g. fieldConfig.description */}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            );
          })}
          <div className="flex flex-col items-center justify-center">
            {!isPublished && (
              <Alert variant="default" className="w-full">
                <AlertTitle>We are no longer accepting applications for this program.</AlertTitle>
              </Alert>
            )}
            <Button
              disabled={!isPublished || form.formState.isSubmitting}
              style={buttonStyle}
              className={cn(
                isDesktop
                  ? 'w-full py-3 rounded-lg text-lg font-semibold mt-2'
                  : 'w-full py-2 rounded-lg text-base font-semibold mt-2',
                !isPublished && 'opacity-50 cursor-not-allowed',
              )}
            >
              {form.formState.isSubmitting ? 'Submitting...' : buttonLabel}
            </Button>
          </div>

          <div
            className={isDesktop ? 'flex flex-col items-center mt-4 gap-1' : 'flex flex-col items-center mt-3 gap-1'}
          >
            <span className="font-bold text-sm">{footerBrand}</span>
            <div className="flex gap-4 text-xs">
              {footerLinks.map((link) => (
                <a key={link.label} href={link.href} target="_blank" rel="noopener noreferrer" className="underline hover:text-[#5b2323]">
                  {link.label}
                </a>
              ))}
            </div>
          </div>
        </form>
      </Form>
      {/* Social Media Section */}
      {socialMedia.length > 0 && (
        <div
          className={
            isDesktop
              ? 'absolute bottom-0 left-0 w-full flex justify-center items-center px-2 gap-2 py-4 z-50'
              : 'w-full flex justify-center items-center px-2 gap-2 py-4'
          }
          style={{ background: isDesktop ? backgroundColor : undefined, color: bodyTextColor }}
        >
          <P>Find us on</P>
          <div className="flex gap-2">
            {socialMedia.map((social) => (
              <a
                key={social.platform}
                href={social.url}
                target="_blank"
                rel="noopener noreferrer"
                className="cursor-pointer"
                style={{ color: bodyTextColor }}
              >
                {getSocialIcon(social.platform)}
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
